/**
 * Deterministic APVMA deep links, built from a product's APVMA approval
 * number (PUBCRIS `pcode`).
 *
 * The approved label PDF is published by the APVMA itself at a fixed path —
 * `<pcode>ELBL.pdf` — so no lookup, no stored URL and no manufacturer hunt is
 * needed. Verified against a spread of 73 registered products across the whole
 * register: every one returned a 200 `application/pdf`.
 *
 * Note the key is the PRODUCT number, not the label registration number that
 * PUBCRIS lists on its Labels tab (`chemicals.label_reg_number`) — that one
 * 404s.
 */
const ELABEL_BASE = 'https://elabels.apvma.gov.au';
const PUBCRIS_BASE = 'https://portal.apvma.gov.au/pubcris';

/** Digits-only approval number, or null when there isn't a usable one. */
function pcode(approvalNumber?: string | null): string | null {
  const digits = String(approvalNumber ?? '').replace(/\D/g, '');
  return digits || null;
}

/** The APVMA-hosted approved label PDF for a product. */
export function apvmaLabelUrl(approvalNumber?: string | null): string | null {
  const code = pcode(approvalNumber);
  return code ? `${ELABEL_BASE}/${code}ELBL.pdf` : null;
}

/**
 * The PUBCRIS product record — actives, pack sizes, label numbers, per-state
 * registration, withholding text and the full host/pest list.
 *
 * This is a Liferay portlet action URL. It resolves without the `p_auth` CSRF
 * token that the portal's own links carry, which is what makes it linkable.
 */
export function apvmaPubcrisUrl(approvalNumber?: string | null): string | null {
  const code = pcode(approvalNumber);
  if (!code) return null;
  const q = new URLSearchParams({
    p_p_id: 'pubcrisportlet_WAR_pubcrisportlet',
    p_p_lifecycle: '1',
    p_p_state: 'normal',
    p_p_mode: 'view',
    _pubcrisportlet_WAR_pubcrisportlet_id: code,
    '_pubcrisportlet_WAR_pubcrisportlet_javax.portlet.action': 'viewProduct',
  });
  return `${PUBCRIS_BASE}?${q.toString()}`;
}
